// PortfolioWizard.jsx - Portfolio creation wizard for Foundations
import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

const PortfolioWizard = ({ isOpen, onClose, onComplete, availableCompanies = [] }) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    // Step 1: Basic Information
    name: '',
    description: '',
    groupType: 'portfolio',
    
    // Step 2: Companies
    selectedCompanies: [],
    
    // Step 3: Settings
    riskThresholds: {
      lowRisk: 30,
      mediumRisk: 50,
      highRisk: 70
    },
    notifications: {
      emailAlerts: true,
      riskThresholdAlerts: true,
      assessmentReminders: true
    },
    
    // Step 4: Review
    confirmed: false
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Steps configuration
  const steps = [
    {
      number: 1,
      title: 'Basic Information',
      description: 'Name and configure your portfolio',
      required: true
    },
    {
      number: 2,
      title: 'Select Companies',
      description: 'Add companies to your portfolio',
      required: false
    },
    {
      number: 3,
      title: 'Configure Settings',
      description: 'Set risk thresholds and notifications',
      required: false
    },
    {
      number: 4,
      title: 'Review & Create',
      description: 'Review and confirm your portfolio',
      required: true
    }
  ];

  const groupTypes = [
    { 
      value: 'portfolio', 
      label: 'Portfolio', 
      description: 'General investment or management portfolio',
      color: 'text-blue-600'
    },
    { 
      value: 'captive', 
      label: 'Insurance Captive', 
      description: 'Insurance captive risk management group',
      color: 'text-green-600'
    },
    { 
      value: 'mssp', 
      label: 'MSSP Portfolio', 
      description: 'Managed Security Service Provider clients',
      color: 'text-purple-600'
    },
    { 
      value: 'custom', 
      label: 'Custom Group', 
      description: 'Custom business grouping',
      color: 'text-orange-600'
    }
  ];

  // Validation functions
  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 1:
        if (!wizardData.name.trim()) {
          newErrors.name = 'Portfolio name is required';
        } else if (wizardData.name.trim().length < 3) {
          newErrors.name = 'Portfolio name must be at least 3 characters';
        }
        break;
      
      case 2:
        // Companies are optional, but if selected, validate count
        if (wizardData.selectedCompanies.length > 50) {
          newErrors.companies = 'Maximum 50 companies allowed per portfolio';
        }
        break;
        
      case 3:
        if (wizardData.riskThresholds.lowRisk >= wizardData.riskThresholds.mediumRisk) {
          newErrors.thresholds = 'Low risk threshold must be less than medium risk threshold';
        }
        if (wizardData.riskThresholds.mediumRisk >= wizardData.riskThresholds.highRisk) {
          newErrors.thresholds = 'Medium risk threshold must be less than high risk threshold';
        }
        break;
        
      case 4:
        if (!wizardData.confirmed) {
          newErrors.confirmation = 'Please confirm the portfolio details';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, steps.length));
    }
  };

  const goToPrevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const goToStep = (stepNumber) => {
    if (stepNumber <= currentStep + 1 && stepNumber >= 1) {
      setCurrentStep(stepNumber);
    }
  };

  // Data update handlers
  const updateWizardData = (updates) => {
    setWizardData(prev => ({ ...prev, ...updates }));
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(updates).forEach(key => {
        delete newErrors[key];
      });
      return newErrors;
    });
  };

  // Company selection handlers
  const handleCompanyToggle = (company) => {
    const isSelected = wizardData.selectedCompanies.some(c => c.company_id === company.company_id);
    
    if (isSelected) {
      updateWizardData({
        selectedCompanies: wizardData.selectedCompanies.filter(c => c.company_id !== company.company_id)
      });
    } else {
      updateWizardData({
        selectedCompanies: [...wizardData.selectedCompanies, company]
      });
    }
  };

  // Final submission
  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    try {
      setIsSubmitting(true);
      
      // Prepare the final data
      const portfolioData = {
        name: wizardData.name.trim(),
        description: wizardData.description.trim(),
        groupType: wizardData.groupType,
        riskThresholds: wizardData.riskThresholds,
        notifications: wizardData.notifications,
        companyIds: wizardData.selectedCompanies.map(c => c.company_id)
      };
      
      console.log('🚀 PortfolioWizard - Submitting portfolio data:', portfolioData);
      
      // Call the parent completion handler
      await onComplete(portfolioData);
      
    } catch (error) {
      console.error('Error creating portfolio:', error);
      setErrors({ submit: 'Failed to create portfolio. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset wizard when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setWizardData({
        name: '',
        description: '',
        groupType: 'portfolio',
        selectedCompanies: [],
        riskThresholds: { lowRisk: 30, mediumRisk: 50, highRisk: 70 },
        notifications: { emailAlerts: true, riskThresholdAlerts: true, assessmentReminders: true },
        confirmed: false
      });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentStepData = steps.find(s => s.number === currentStep);
  const isLastStep = currentStep === steps.length;
  const isFirstStep = currentStep === 1;

  return (
    <Dialog.Root open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 ra-glass rounded-lg shadow-[0_20px_25px_rgba(0,0,0,0.4)] max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <Dialog.Title className="text-2xl font-bold">Create New Portfolio</Dialog.Title>
              <p className="text-blue-100 mt-1">{currentStepData.description}</p>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="p-2 hover:ra-glass hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              </button>
            </Dialog.Close>
          </div>
          
          {/* Progress Steps */}
          <div className="mt-6 flex space-x-4">
            {steps.map((step, index) => {
              const isActive = step.number === currentStep;
              const isCompleted = step.number < currentStep;
              const isAccessible = step.number <= currentStep + 1;
              
              return (
                <div
                  key={step.number}
                  className={`flex items-center cursor-pointer ${
                    isAccessible ? 'hover:opacity-80' : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => isAccessible && goToStep(step.number)}
                >
                  <div className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all ${
                    isActive 
                      ? 'ra-glass bg-opacity-20 text-white' 
                      : isCompleted 
                        ? 'bg-green-500 bg-opacity-80 text-white'
                        : 'text-blue-200 hover:text-white'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isActive 
                        ? 'ra-glass bg-opacity-20' 
                        : isCompleted 
                          ? 'ra-glass bg-opacity-30'
                          : 'bg-transparent border border-current'
                    }`}>
                      {isCompleted ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="hidden md:block">
                      <div className="font-medium">{step.title}</div>
                      <div className="text-xs opacity-80">{step.number} of {steps.length}</div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <svg className="w-5 h-5 text-blue-200 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Portfolio Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                      Portfolio Name *
                    </label>
                    <input
                      type="text"
                      value={wizardData.name}
                      onChange={(e) => updateWizardData({ name: e.target.value })}
                      aria-label="Portfolio Name"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-transparent ${
                        errors.name ? 'border-red-300' : 'border-[rgba(255,255,255,0.18)]'
                      }`}
                      placeholder="e.g., Technology Portfolio, ABC Insurance Captive"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                      Description
                    </label>
                    <textarea
                      value={wizardData.description}
                      onChange={(e) => updateWizardData({ description: e.target.value })}
                      aria-label="Description"
                      className="w-full px-3 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-transparent"
                      rows="3"
                      placeholder="Describe the purpose and scope of this portfolio..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#ccd6f6] mb-3">
                      Portfolio Type *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {groupTypes.map((type) => {
                        return (
                          <label
                            key={type.value}
                            className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              wizardData.groupType === type.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.18)]'
                            }`}
                          >
                            <input
                              type="radio"
                              name="groupType"
                              value={type.value}
                              checked={wizardData.groupType === type.value}
                              onChange={(e) => updateWizardData({ groupType: e.target.value })}
                              aria-label={type.label}
                              className="sr-only"
                            />
                            <div className="flex items-start">
                              <div className={`w-5 h-5 rounded-full mr-3 mt-0.5 ${
                                wizardData.groupType === type.value ? 'bg-blue-500' : 'bg-[rgba(255,255,255,0.08)]'
                              }`}>
                                {wizardData.groupType === type.value && (
                                  <div className="w-full h-full rounded-full bg-blue-500 flex items-center justify-center">
                                    <div className="w-2 h-2 ra-glass rounded-full"></div>
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-[#e6f1ff]">{type.label}</div>
                                <div className="text-sm text-[#a8b2d1]">{type.description}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Companies */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">Select Companies</h3>
                <p className="text-[#a8b2d1] mb-4">
                  Choose companies to add to your portfolio. You can always add more later.
                </p>
                
                {errors.companies && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{errors.companies}</p>
                  </div>
                )}

                <div className="border border-[rgba(255,255,255,0.12)] rounded-lg">
                  <div className="p-4 border-b border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-[#e6f1ff]">Available Companies</h4>
                        <p className="text-sm text-[#a8b2d1]">
                          {availableCompanies.length} companies available • {wizardData.selectedCompanies.length} selected
                        </p>
                      </div>
                      {wizardData.selectedCompanies.length > 0 && (
                        <button
                          onClick={() => updateWizardData({ selectedCompanies: [] })}
                          className="text-sm text-red-600 hover:text-[#ccd6f6]"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {availableCompanies.length === 0 ? (
                      <div className="p-8 text-center text-[#8892b0]">
                        <div className="mb-2">
                          <svg className="w-12 h-12 mx-auto text-[#5a6a8a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <p>No companies available in your portfolio access list.</p>
                        <p className="text-sm mt-1">Contact your administrator to get access to client companies.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[rgba(255,255,255,0.12)]">
                        {availableCompanies.map((company) => {
                          const isSelected = wizardData.selectedCompanies.some(c => c.company_id === company.company_id);
                          return (
                            <div
                              key={company.company_id}
                              className="p-4 hover:bg-[rgba(255,255,255,0.03)] cursor-pointer"
                              onClick={() => handleCompanyToggle(company)}
                            >
                              <div className="flex items-center">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 ${
                                  isSelected 
                                    ? 'bg-blue-500 border-blue-500' 
                                    : 'border-[rgba(255,255,255,0.18)] hover:border-blue-500'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-[#e6f1ff]">{company.company_name}</div>
                                  <div className="text-sm text-[#8892b0]">
                                    {company.latest_assessment?.industry_vertical && (
                                      <span>{company.latest_assessment.industry_vertical}</span>
                                    )}
                                    {company.latest_assessment?.annual_revenue && (
                                      <span className="ml-2">• Revenue: ${(company.latest_assessment.annual_revenue / 1000000).toFixed(1)}M</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Configure Settings */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Portfolio Settings</h3>
                
                {errors.thresholds && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{errors.thresholds}</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Risk Thresholds */}
                  <div>
                    <label className="block text-sm font-medium text-[#ccd6f6] mb-3">
                      Risk Score Thresholds
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-[#a8b2d1] mb-1">Low Risk (Green)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={wizardData.riskThresholds.lowRisk}
                          onChange={(e) => updateWizardData({
                            riskThresholds: {
                              ...wizardData.riskThresholds,
                              lowRisk: parseInt(e.target.value) || 0
                            }
                          })}
                          aria-label="Low Risk threshold"
                          className="w-full px-3 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                        />
                        <p className="text-xs text-[#8892b0] mt-1">0 - {wizardData.riskThresholds.lowRisk}</p>
                      </div>
                      <div>
                        <label className="block text-xs text-[#a8b2d1] mb-1">Medium Risk (Yellow)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={wizardData.riskThresholds.mediumRisk}
                          onChange={(e) => updateWizardData({
                            riskThresholds: {
                              ...wizardData.riskThresholds,
                              mediumRisk: parseInt(e.target.value) || 0
                            }
                          })}
                          aria-label="Medium Risk threshold"
                          className="w-full px-3 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                        />
                        <p className="text-xs text-[#8892b0] mt-1">{wizardData.riskThresholds.lowRisk} - {wizardData.riskThresholds.mediumRisk}</p>
                      </div>
                      <div>
                        <label className="block text-xs text-[#a8b2d1] mb-1">High Risk (Red)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={wizardData.riskThresholds.highRisk}
                          onChange={(e) => updateWizardData({
                            riskThresholds: {
                              ...wizardData.riskThresholds,
                              highRisk: parseInt(e.target.value) || 0
                            }
                          })}
                          aria-label="High Risk threshold"
                          className="w-full px-3 py-2 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)]"
                        />
                        <p className="text-xs text-[#8892b0] mt-1">{wizardData.riskThresholds.mediumRisk} - {wizardData.riskThresholds.highRisk}</p>
                      </div>
                    </div>
                  </div>

                  {/* Notifications */}
                  <div>
                    <label className="block text-sm font-medium text-[#ccd6f6] mb-3">
                      Notification Settings
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={wizardData.notifications.emailAlerts}
                          onChange={(e) => updateWizardData({
                            notifications: {
                              ...wizardData.notifications,
                              emailAlerts: e.target.checked
                            }
                          })}
                          aria-label="Email alerts for portfolio changes"
                          className="rounded border-[rgba(255,255,255,0.18)] text-blue-600 shadow-[0_1px_3px_rgba(0,0,0,0.3)] focus:border-[#00d9ff] focus:ring focus:ring-[rgba(0,217,255,0.3)] focus:ring-opacity-50"
                        />
                        <span className="ml-3 text-sm text-[#ccd6f6]">Email alerts for portfolio changes</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={wizardData.notifications.riskThresholdAlerts}
                          onChange={(e) => updateWizardData({
                            notifications: {
                              ...wizardData.notifications,
                              riskThresholdAlerts: e.target.checked
                            }
                          })}
                          aria-label="Alerts when companies exceed risk thresholds"
                          className="rounded border-[rgba(255,255,255,0.18)] text-blue-600 shadow-[0_1px_3px_rgba(0,0,0,0.3)] focus:border-[#00d9ff] focus:ring focus:ring-[rgba(0,217,255,0.3)] focus:ring-opacity-50"
                        />
                        <span className="ml-3 text-sm text-[#ccd6f6]">Alerts when companies exceed risk thresholds</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={wizardData.notifications.assessmentReminders}
                          onChange={(e) => updateWizardData({
                            notifications: {
                              ...wizardData.notifications,
                              assessmentReminders: e.target.checked
                            }
                          })}
                          aria-label="Reminders for overdue assessments"
                          className="rounded border-[rgba(255,255,255,0.18)] text-blue-600 shadow-[0_1px_3px_rgba(0,0,0,0.3)] focus:border-[#00d9ff] focus:ring focus:ring-[rgba(0,217,255,0.3)] focus:ring-opacity-50"
                        />
                        <span className="ml-3 text-sm text-[#ccd6f6]">Reminders for overdue assessments</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Create */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e6f1ff] mb-4">Review Portfolio</h3>
                
                {errors.confirmation && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{errors.confirmation}</p>
                  </div>
                )}

                {errors.submit && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Basic Information Review */}
                  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-4">
                    <h4 className="font-semibold text-[#e6f1ff] mb-3">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#a8b2d1]">Name:</span>
                        <div className="font-medium">{wizardData.name}</div>
                      </div>
                      <div>
                        <span className="text-[#a8b2d1]">Type:</span>
                        <div className="font-medium">
                          {groupTypes.find(t => t.value === wizardData.groupType)?.label}
                        </div>
                      </div>
                      {wizardData.description && (
                        <div className="col-span-2">
                          <span className="text-[#a8b2d1]">Description:</span>
                          <div className="font-medium">{wizardData.description}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Companies Review */}
                  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-4">
                    <h4 className="font-semibold text-[#e6f1ff] mb-3">
                      Companies ({wizardData.selectedCompanies.length})
                    </h4>
                    {wizardData.selectedCompanies.length === 0 ? (
                      <p className="text-[#a8b2d1] text-sm">No companies selected. You can add companies after creating the portfolio.</p>
                    ) : (
                      <div className="space-y-2">
                        {wizardData.selectedCompanies.slice(0, 5).map(company => (
                          <div key={company.company_id} className="flex justify-between items-center text-sm">
                            <span className="font-medium">{company.company_name}</span>
                            <span className="text-[#a8b2d1]">{company.latest_assessment?.industry_vertical || 'N/A'}</span>
                          </div>
                        ))}
                        {wizardData.selectedCompanies.length > 5 && (
                          <p className="text-xs text-[#8892b0]">
                            ...and {wizardData.selectedCompanies.length - 5} more companies
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Settings Review */}
                  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-4">
                    <h4 className="font-semibold text-[#e6f1ff] mb-3">Settings</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-[#a8b2d1]">Risk Thresholds:</span>
                        <div className="font-medium">
                          Low: 0-{wizardData.riskThresholds.lowRisk}, 
                          Medium: {wizardData.riskThresholds.lowRisk}-{wizardData.riskThresholds.mediumRisk}, 
                          High: {wizardData.riskThresholds.mediumRisk}-{wizardData.riskThresholds.highRisk}, 
                          Critical: {wizardData.riskThresholds.highRisk}+
                        </div>
                      </div>
                      <div>
                        <span className="text-[#a8b2d1]">Notifications:</span>
                        <div className="font-medium">
                          {Object.entries(wizardData.notifications)
                            .filter(([_, enabled]) => enabled)
                            .map(([key]) => key.replace(/([A-Z])/g, ' $1').toLowerCase())
                            .join(', ') || 'None enabled'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confirmation */}
                  <div className="border-t border-[rgba(255,255,255,0.12)] pt-4">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={wizardData.confirmed}
                        onChange={(e) => updateWizardData({ confirmed: e.target.checked })}
                        aria-label="I confirm that the portfolio information is correct"
                        className="rounded border-[rgba(255,255,255,0.18)] text-blue-600 shadow-[0_1px_3px_rgba(0,0,0,0.3)] focus:border-[#00d9ff] focus:ring focus:ring-[rgba(0,217,255,0.3)] focus:ring-opacity-50 mt-1"
                      />
                      <span className="ml-3 text-sm text-[#ccd6f6]">
                        I confirm that the portfolio information is correct and I want to create this portfolio.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[rgba(255,255,255,0.12)] p-6 bg-[rgba(255,255,255,0.03)]">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={goToPrevStep}
                disabled={isFirstStep}
                className="flex items-center px-4 py-2 text-[#a8b2d1] border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[#172a45] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Previous
              </button>
              
              <span className="text-sm text-[#8892b0]">
                Step {currentStep} of {steps.length}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-[#a8b2d1] border border-[rgba(255,255,255,0.18)] rounded-lg hover:bg-[#172a45]"
              >
                Cancel
              </button>
              
              {isLastStep ? (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Create Portfolio
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={goToNextStep}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PortfolioWizard;